/**
 * Place Detail Module - Native Implementation (COMPLETE PORT)
 * Full-featured place detail card with photos, map, reviews, and enrichment
 *
 * Features (all from original PlaceCard):
 * - Photo gallery with modal viewer
 * - Map integration with modal
 * - Reviews modal (Google reviews)
 * - Editable description (About section)
 * - Chips (rating, status, price level)
 * - Sections (About, Amenities, Address, Hours, Phone)
 * - Action buttons (Website, Directions, Reviews, Call, Share)
 * - Citations/sources
 * - Artwork mode (minimal rendering for art pieces)
 * - All modals and interactions from original
 */

import React, { useState, useRef, useEffect } from "react";
import type { Module } from "@/types/modules";
import { useSession } from "@/features/auth/hooks/useSession";
import { useUpdateObject } from "@/features/objects/hooks/mutations";
import { useQueryClient, useQuery } from "@tanstack/react-query";
import { queryKeys } from "@/lib/query/keys";
import { http } from "@/lib/http";
import { api } from "@/lib/api";
import { BREAKPOINTS } from "@/lib/breakpoints";
import "../_infrastructure/ModuleStyles.css";
import PlaceCard from "./PlaceCard";

interface PlaceDetailModuleProps {
  module: Module;
  conversationId?: string;
  onClose?: () => void;
  onNoteSaved?: (noteText: string) => void;
  [legacyProp: string]: unknown;
}

interface PlaceData {
  objectId?: string;
  etag?: string;
  placeId?: string;
  isArtwork?: boolean;
  galleryPhotos?: Array<{
    fullsizeUrl: string;
    thumbnailUrl?: string;
    caption?: string;
    isMap?: boolean;
  }>;
  title: string;
  subtitle?: string;
  chips?: string[];
  images?: Array<{
    fullsizeUrl: string;
    thumbnailUrl?: string;
    caption?: string;
    isMap?: boolean;
  }>;
  sections?: Array<{
    title: string;
    body?: string;
    items?: Array<{
      label?: string;
      value: string;
    }>;
  }>;
  actions?: Array<{
    kind: string;
    url: string;
    label: string;
  }>;
  citations?: Array<{
    title?: string;
    url: string;
  }>;
  userNotes?: string;
  userRating?: number;
  imageUrl?: string;
}

export default function PlaceDetailModule(props: PlaceDetailModuleProps) {
  const {
    module,
    conversationId: explicitConversationId,
    onClose,
    onNoteSaved,
  } = props;
  const conversationId = explicitConversationId ?? "";
  const { token } = useSession();
  const queryClient = useQueryClient();

  // View state - controls which page is shown
  const [currentView, setCurrentView] = useState<
    "main" | "photos" | "reviews" | "directions"
  >("main");
  const [photoModalIndex, setPhotoModalIndex] = useState(0);
  const [isMapModalOpen, setMapModalOpen] = useState(false);

  // Description editing state
  const [isEditingDescription, setIsEditingDescription] = useState(false);
  const [editedDescription, setEditedDescription] = useState("");

  // Personal notes state
  const [isEditingNotes, setIsEditingNotes] = useState(false);
  const [editedNotes, setEditedNotes] = useState("");

  // Personal rating state (initialized after placeData below)
  const [localRating, setLocalRating] = useState<number | undefined>(undefined);
  const [ratingSaved, setRatingSaved] = useState(false);
  const ratingTouchedRef = useRef(false); // prevents cache re-render from overwriting user's drag
  const ratingSaveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null); // debounce rating save

  // Copy permalink state
  const [copiedLink, setCopiedLink] = useState(false);

  // Gear menu state
  const [showGearMenu, setShowGearMenu] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const gearMenuRef = useRef<HTMLDivElement>(null);

  // Close gear menu on outside click
  useEffect(() => {
    if (!showGearMenu) return;
    const handleClick = (e: MouseEvent) => {
      if (
        gearMenuRef.current &&
        !gearMenuRef.current.contains(e.target as Node)
      ) {
        setShowGearMenu(false);
      }
    };
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, [showGearMenu]);

  // Refs for auto-scrolling thumbnail strip
  const thumbnailStripRef = React.useRef<HTMLDivElement>(null);
  const thumbnailRefs = React.useRef<(HTMLButtonElement | null)[]>([]);

  // Get place data from module config
  const placeData = (module.config as PlaceData) || {};

  // Sync local rating from placeData on mount — skip if user has touched the slider
  useEffect(() => {
    if (ratingTouchedRef.current) return;
    if ((placeData as any).userRating != null) {
      setLocalRating((placeData as any).userRating);
    }
  }, [(placeData as any).userRating]);

  // Initialize mutation hook for updating place description
  // ALWAYS call hooks unconditionally - never conditionally
  const updateMutation = useUpdateObject();

  // Handle action buttons (Website, Directions, Call)
  const handleAction = (action: {
    kind: string;
    url: string;
    label: string;
  }) => {
    if (action.kind === "link" || action.kind === "map") {
      window.open(action.url, "_blank", "noopener,noreferrer");
    } else if (action.kind === "call") {
      window.location.href = action.url;
    }
  };

  // Handle share — both URLs in text field for reliable cross-platform delivery
  const handleShare = () => {
    const mapsAction = placeData.actions?.find((a) => a.kind === "map");
    const websiteAction = placeData.actions?.find((a) => a.kind === "link");
    const textParts: string[] = [];
    if (websiteAction?.url) textParts.push(websiteAction.url);
    if (mapsAction?.url) textParts.push(mapsAction.url);
    if (textParts.length === 0) textParts.push(window.location.href);
    if (navigator.share) {
      navigator
        .share({
          text: textParts.join("\n"),
        })
        .catch(() => {
          // User cancelled share - silent fail
        });
    } else {
      alert("Sharing not supported on this device");
    }
  };

  // Handle delete place
  const handleDeletePlace = async () => {
    if (!placeData.objectId) return;
    try {
      await api.objects.deleteObject(
        token ?? null,
        "place",
        placeData.objectId,
      );
      queryClient.invalidateQueries({
        queryKey: queryKeys.objects.byType("place"),
      });
      setShowDeleteConfirm(false);
      if (onClose) onClose();
    } catch (err) {
      console.error("[PlaceDetail] Failed to delete:", err);
      alert("Failed to delete place.");
    }
  };

  // Open photo gallery view
  const openPhotoGallery = (index: number) => {
    setPhotoModalIndex(index);
    setCurrentView("photos");
  };

  // Handle edit description
  const handleEditDescription = (currentDescription: string) => {
    setEditedDescription(currentDescription);
    setIsEditingDescription(true);
  };

  // Handle cancel edit
  const handleCancelEdit = () => {
    setIsEditingDescription(false);
    setEditedDescription("");
  };

  // Handle save description
  const handleSaveDescription = async () => {
    if (!placeData.objectId) {
      alert("Cannot save: missing object ID");
      return;
    }

    try {
      await updateMutation.mutateAsync({
        objectType: "place",
        objectId: placeData.objectId,
        data: {
          description: editedDescription.trim(),
        },
      });

      // Invalidate queries to refetch updated data
      queryClient.invalidateQueries({
        queryKey: queryKeys.objects.detail(placeData.objectId),
      });
      queryClient.invalidateQueries({
        queryKey: queryKeys.conversations.detail(conversationId),
      });

      setIsEditingDescription(false);
    } catch (error) {
      console.error("[PlaceDetailModule] Failed to save description:", error);
      alert("Failed to save description. Please try again.");
    }
  };

  // Handle save personal notes
  const handleSaveNotes = async () => {
    if (!placeData.objectId) return;
    try {
      await updateMutation.mutateAsync({
        objectType: "place",
        objectId: placeData.objectId,
        data: { attributes: { userNotes: editedNotes.trim() } },
      });
      // Update local config so note shows immediately
      placeData.userNotes = editedNotes.trim();
      queryClient.invalidateQueries({
        queryKey: queryKeys.objects.detail(placeData.objectId),
      });
      queryClient.invalidateQueries({
        queryKey: queryKeys.conversations.detail(conversationId),
      });
      setIsEditingNotes(false);
    } catch (error) {
      console.error("[PlaceDetailModule] Failed to save notes:", error);
    }
  };

  // Handle copy permalink
  const handleCopyPermalink = async () => {
    const url = (placeData as any).publish?.url || placeData.imageUrl;
    if (!url || !url.startsWith("https://")) return;
    try {
      await navigator.clipboard.writeText(url);
      setCopiedLink(true);
      setTimeout(() => setCopiedLink(false), 2000);
    } catch (err) {
      console.error("[PlaceDetailModule] Failed to copy permalink:", err);
    }
  };

  // Get gallery photos
  const galleryPhotos =
    placeData.galleryPhotos ||
    placeData.images?.filter((img) => !img.isMap) ||
    [];

  // Get specific action buttons
  const websiteAction = placeData.actions?.find((a) => a.kind === "link");
  const directionsAction = placeData.actions?.find((a) => a.kind === "map");
  const callAction = placeData.actions?.find((a) => a.kind === "call");

  // Check if this is a minimal artwork card
  const isArtwork = placeData.isArtwork === true;
  const image = placeData.images?.[0];

  // Fetch reviews data - MUST be at top level, not conditional
  const {
    data: reviewsData,
    isLoading: reviewsLoading,
    error: reviewsError,
  } = useQuery({
    queryKey: queryKeys.agent.enrichPlace(placeData.placeId || ""),
    queryFn: async () => {
      const { data } = await http.get<{
        placeName: string;
        rating: number | null;
        totalReviews: number;
        reviews: Array<{
          author: string;
          authorPhotoUri: string | null;
          rating: number;
          text: string;
          relativeTime: string;
          publishTime: string | null;
        }>;
      }>(`/places/${placeData.placeId}/reviews`, { authToken: token });
      return data;
    },
    staleTime: 1000 * 60 * 30, // 30 minutes
    enabled: !!placeData.placeId && currentView === "reviews",
  });

  // Fetch maps embed URL - MUST be at top level, not conditional.
  // The Address section's first item can arrive as a plain string, a
  // { value: string }, or a nested { value: { address } } / { address } object
  // depending on how the place was enriched — coerce to a display string so we
  // never render an object as a React child (#crash).
  const addrRaw = placeData.sections?.find((s) => s.title === "Address")
    ?.items?.[0] as unknown;
  const toAddressString = (v: unknown): string => {
    if (typeof v === "string") return v;
    if (v && typeof v === "object") {
      const o = v as Record<string, unknown>;
      if (typeof o.value === "string") return o.value;
      if (typeof o.address === "string") return o.address;
      if (o.value && typeof o.value === "object") {
        const inner = (o.value as Record<string, unknown>).address;
        if (typeof inner === "string") return inner;
      }
    }
    return "";
  };
  const address = toAddressString(addrRaw);
  const destination = address || placeData.subtitle || placeData.title;

  const { data: mapsData } = useQuery({
    queryKey: ["maps-embed-url", destination],
    queryFn: async () => {
      const { data } = await http.get<{ embedUrl: string }>(
        `/places/maps-embed-url`,
        { authToken: token, searchParams: { destination } },
      );
      return data;
    },
    enabled: !!destination && currentView === "directions",
    staleTime: 1000 * 60 * 60, // Cache for 1 hour
  });

  // Auto-scroll thumbnail strip to show current photo
  React.useEffect(() => {
    if (currentView !== "photos") return;

    const currentThumbnail = thumbnailRefs.current[photoModalIndex];
    const stripContainer = thumbnailStripRef.current;

    if (currentThumbnail && stripContainer) {
      // Get the thumbnail's position relative to the container
      const thumbnailLeft = currentThumbnail.offsetLeft;
      const thumbnailWidth = currentThumbnail.offsetWidth;
      const containerWidth = stripContainer.offsetWidth;

      // Calculate the scroll position to center the thumbnail
      const scrollPosition =
        thumbnailLeft - containerWidth / 2 + thumbnailWidth / 2;

      // Scroll to the calculated position
      stripContainer.scrollTo({
        left: scrollPosition,
        behavior: "smooth",
      });
    }
  }, [photoModalIndex, currentView]);

  // Keyboard navigation for photos - MUST be at top level, not conditional
  React.useEffect(() => {
    // Only handle keyboard events when in photos view
    if (currentView !== "photos" || galleryPhotos.length === 0) {
      return;
    }

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "ArrowLeft" && photoModalIndex > 0) {
        setPhotoModalIndex(photoModalIndex - 1);
      } else if (
        e.key === "ArrowRight" &&
        photoModalIndex < galleryPhotos.length - 1
      ) {
        setPhotoModalIndex(photoModalIndex + 1);
      } else if (e.key === "Escape") {
        if (onClose) {
          onClose();
        } else {
          setCurrentView("main");
        }
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [currentView, photoModalIndex, galleryPhotos.length, onClose]);

  // Minimal artwork rendering: just image and title
  if (isArtwork && image) {
    return (
      <div className="module">
        <div className="overflow-hidden rounded-lg shadow-md bg-gray-100 w-full">
          <div className="relative w-full aspect-[4/3] bg-gray-200">
            <img
              src={image.thumbnailUrl || image.fullsizeUrl}
              alt={placeData.title}
              className="w-full h-full object-cover cursor-pointer"
              onClick={() => openPhotoGallery(0)}
              onError={(e) => {
                e.currentTarget.style.display = "none";
              }}
            />
          </div>
          <div className="p-3">
            <h3 className="text-sm font-semibold text-gray-900 line-clamp-2">
              {placeData.title}
            </h3>
            {placeData.subtitle && (
              <p className="text-xs text-gray-600 mt-1 line-clamp-1">
                {placeData.subtitle}
              </p>
            )}
          </div>
        </div>
      </div>
    );
  }

  // PHOTOS VIEW - Photo gallery
  if (currentView === "photos" && galleryPhotos.length > 0) {
    const currentPhoto = galleryPhotos[photoModalIndex];
    const isMobile =
      typeof window !== "undefined" &&
      window.innerWidth <= BREAKPOINTS.compactMax;

    return (
      <div
        style={{
          height: isMobile ? "100dvh" : "75vh", // Full height on mobile, 75vh on desktop
          width: isMobile ? "100vw" : "100%", // Full width on mobile
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
          position: isMobile ? "fixed" : "relative",
          top: isMobile ? 0 : "auto",
          left: isMobile ? 0 : "auto",
          right: isMobile ? 0 : "auto",
          bottom: isMobile ? 0 : "auto",
          borderRadius: isMobile ? "0" : "12px",
          zIndex: isMobile ? 10000 : "auto",
        }}
      >
        {/* Header with back button */}
        <div
          style={{
            position: "absolute",
            top: "16px",
            left: "16px",
            right: "16px",
            zIndex: 10,
            display: "flex",
            alignItems: "center",
            gap: "8px",
          }}
        >
          <button
            onClick={() => setCurrentView("main")}
            style={{
              background: "var(--color-surface)",
              border: "none",
              borderRadius: "50%",
              width: "32px",
              height: "32px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              cursor: "pointer",
              boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
            }}
            title="Back"
          >
            <svg
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
            >
              <line x1="19" y1="12" x2="5" y2="12"></line>
              <polyline points="12 19 5 12 12 5"></polyline>
            </svg>
          </button>
        </div>

        {/* Copy link button for current photo — positioned below back button on left */}
        <button
          onClick={async () => {
            const url =
              currentPhoto.fullsizeUrl || currentPhoto.thumbnailUrl || "";
            if (!url) return;
            try {
              await navigator.clipboard.writeText(url);
              setCopiedLink(true);
              setTimeout(() => setCopiedLink(false), 2000);
            } catch {
              /* clipboard unavailable */
            }
          }}
          style={{
            position: "absolute",
            top: "56px",
            left: "16px",
            zIndex: 10,
            background: copiedLink ? "var(--color-primary)" : "white",
            color: copiedLink ? "white" : "#333",
            border: "none",
            borderRadius: "8px",
            padding: "8px 14px",
            display: "flex",
            alignItems: "center",
            gap: "6px",
            cursor: "pointer",
            boxShadow: "0 2px 12px rgba(0,0,0,0.15)",
            transition: "all 0.2s",
            fontSize: "13px",
            fontWeight: 500,
          }}
          title={copiedLink ? "Copied!" : "Copy image link"}
        >
          {copiedLink ? (
            <>
              <svg
                width="18"
                height="18"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.5"
              >
                <polyline points="20 6 9 17 4 12"></polyline>
              </svg>
              Copied!
            </>
          ) : (
            <>
              <svg
                width="18"
                height="18"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" />
                <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
              </svg>
              Copy Link
            </>
          )}
        </button>

        {/* Main photo - constrained to avoid vertical scrolling */}
        <div
          style={{
            flex: 1,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: isMobile ? "60px 16px 0px" : "60px 80px 0px",
            background: "var(--color-surface-hover)",
            overflow: "hidden",
            minHeight: 0, // Critical for flexbox to respect constraints
            borderRadius: isMobile ? "0" : "12px 12px 0 0", // Rounded top corners on desktop only
          }}
        >
          <img
            src={currentPhoto.fullsizeUrl}
            alt={currentPhoto.caption || `Photo ${photoModalIndex + 1}`}
            style={{
              maxWidth: "100%",
              maxHeight: "100%",
              width: "auto",
              height: "auto",
              objectFit: "contain",
              borderRadius: "8px",
            }}
          />
        </div>

        {/* Navigation arrows */}
        {photoModalIndex > 0 && (
          <button
            onClick={(e) => {
              e.stopPropagation();
              setPhotoModalIndex(photoModalIndex - 1);
            }}
            style={{
              position: "absolute",
              left: "16px",
              top: "50%",
              transform: "translateY(-50%)",
              background: "var(--color-surface)",
              border: "1px solid var(--color-border)",
              borderRadius: "50%",
              width: "48px",
              height: "48px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              cursor: "pointer",
              boxShadow: "0 2px 12px rgba(0,0,0,0.1)",
              transition: "all 0.2s",
            }}
            onMouseEnter={(e) =>
              (e.currentTarget.style.background = "var(--color-surface-muted)")
            }
            onMouseLeave={(e) => (e.currentTarget.style.background = "white")}
          >
            <svg
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
            >
              <polyline points="15 18 9 12 15 6"></polyline>
            </svg>
          </button>
        )}
        {photoModalIndex < galleryPhotos.length - 1 && (
          <button
            onClick={(e) => {
              e.stopPropagation();
              setPhotoModalIndex(photoModalIndex + 1);
            }}
            style={{
              position: "absolute",
              right: "16px",
              top: "50%",
              transform: "translateY(-50%)",
              background: "var(--color-surface)",
              border: "1px solid var(--color-border)",
              borderRadius: "50%",
              width: "48px",
              height: "48px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              cursor: "pointer",
              boxShadow: "0 2px 12px rgba(0,0,0,0.1)",
              transition: "all 0.2s",
            }}
            onMouseEnter={(e) =>
              (e.currentTarget.style.background = "var(--color-surface-muted)")
            }
            onMouseLeave={(e) => (e.currentTarget.style.background = "white")}
          >
            <svg
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
            >
              <polyline points="9 18 15 12 9 6"></polyline>
            </svg>
          </button>
        )}

        {/* Thumbnail strip - fixed at bottom, with auto-scroll */}
        <div
          ref={thumbnailStripRef}
          style={{
            height: "120px",
            flexShrink: 0, // Don't let this shrink
            background: "var(--color-surface)",
            borderTop: "1px solid var(--color-border)",
            overflowX: "auto",
            overflowY: "hidden",
            padding: "12px",
            borderRadius: isMobile ? "0" : "0 0 12px 12px", // Rounded bottom corners on desktop only
          }}
        >
          <div style={{ display: "flex", gap: "8px", height: "100%" }}>
            {galleryPhotos.map((photo, idx) => (
              <button
                key={idx}
                ref={(el) => {
                  thumbnailRefs.current[idx] = el;
                }}
                onClick={() => setPhotoModalIndex(idx)}
                style={{
                  flex: "0 0 auto",
                  height: "100%",
                  aspectRatio: "1",
                  border:
                    idx === photoModalIndex
                      ? "2px solid var(--color-primary)"
                      : "2px solid transparent",
                  borderRadius: "8px",
                  overflow: "hidden",
                  cursor: "pointer",
                  opacity: idx === photoModalIndex ? 1 : 0.6,
                  transition: "all 0.2s",
                }}
              >
                <img
                  src={photo.thumbnailUrl}
                  alt={`Thumbnail ${idx + 1}`}
                  style={{ width: "100%", height: "100%", objectFit: "cover" }}
                />
              </button>
            ))}
          </div>
        </div>
      </div>
    );
  }

  // REVIEWS VIEW - Google Reviews
  if (currentView === "reviews") {
    const isMobile =
      typeof window !== "undefined" &&
      window.innerWidth <= BREAKPOINTS.compactMax;

    // Render star rating
    const renderStars = (rating: number) => {
      return (
        <div style={{ display: "flex", gap: "2px" }}>
          {[1, 2, 3, 4, 5].map((star) => (
            <svg
              key={star}
              width="16"
              height="16"
              viewBox="0 0 20 20"
              fill={star <= rating ? "#fbbf24" : "var(--color-border-strong)"}
            >
              <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
            </svg>
          ))}
        </div>
      );
    };

    return (
      <div
        style={{
          height: isMobile ? "100dvh" : "75vh", // Full height on mobile, 75vh on desktop
          width: isMobile ? "100vw" : "100%", // Full width on mobile
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
          position: isMobile ? "fixed" : "relative",
          top: isMobile ? 0 : "auto",
          left: isMobile ? 0 : "auto",
          right: isMobile ? 0 : "auto",
          bottom: isMobile ? 0 : "auto",
          background: "var(--color-surface)",
          borderRadius: isMobile ? "0" : "12px",
          zIndex: isMobile ? 10000 : "auto",
        }}
      >
        {/* Header with back button */}
        <div
          style={{
            position: "absolute",
            top: "16px",
            left: "16px",
            right: "16px",
            zIndex: 10,
            display: "flex",
            alignItems: "center",
            gap: "8px",
          }}
        >
          <button
            onClick={() => setCurrentView("main")}
            style={{
              background: "var(--color-surface)",
              border: "none",
              borderRadius: "50%",
              width: "32px",
              height: "32px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              cursor: "pointer",
              boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
            }}
            title="Back"
          >
            <svg
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
            >
              <line x1="19" y1="12" x2="5" y2="12"></line>
              <polyline points="12 19 5 12 12 5"></polyline>
            </svg>
          </button>
        </div>

        {/* Content area */}
        <div
          className="xv-scroll"
          style={{
            flex: 1,
            overflowY: "auto",
            padding: "60px 20px 20px",
            minHeight: 0,
          }}
        >
          <h2
            style={{
              fontSize: "24px",
              fontWeight: "600",
              marginBottom: "12px",
            }}
          >
            {reviewsData?.placeName || placeData.title}
          </h2>

          {reviewsData && reviewsData.rating && (
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "8px",
                marginBottom: "20px",
              }}
            >
              {renderStars(Math.round(reviewsData.rating))}
              <span
                style={{
                  fontSize: "14px",
                  fontWeight: "500",
                  color: "var(--color-text-subtle)",
                }}
              >
                {reviewsData.rating.toFixed(1)}
              </span>
              <span
                style={{ fontSize: "14px", color: "var(--color-text-muted)" }}
              >
                ({reviewsData.totalReviews.toLocaleString()} reviews)
              </span>
            </div>
          )}

          {reviewsLoading && (
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                padding: "60px 20px",
              }}
            >
              <div style={{ textAlign: "center" }}>
                <div
                  style={{
                    width: "48px",
                    height: "48px",
                    border: "3px solid var(--color-border)",
                    borderTop: "3px solid var(--color-primary)",
                    borderRadius: "50%",
                    animation: "spin 1s linear infinite",
                    margin: "0 auto 16px",
                  }}
                ></div>
                <p style={{ color: "var(--color-text-muted)" }}>
                  Loading reviews...
                </p>
              </div>
            </div>
          )}

          {reviewsError && (
            <div style={{ textAlign: "center", padding: "60px 20px" }}>
              <svg
                width="48"
                height="48"
                viewBox="0 0 24 24"
                fill="none"
                stroke="var(--color-danger)"
                strokeWidth="2"
                style={{ margin: "0 auto 16px" }}
              >
                <circle cx="12" cy="12" r="10"></circle>
                <line x1="12" y1="8" x2="12" y2="12"></line>
                <line x1="12" y1="16" x2="12.01" y2="16"></line>
              </svg>
              <p style={{ fontWeight: "500", color: "var(--color-danger)" }}>
                Failed to load reviews
              </p>
              <p
                style={{
                  fontSize: "14px",
                  color: "var(--color-text-muted)",
                  marginTop: "4px",
                }}
              >
                {reviewsError instanceof Error
                  ? reviewsError.message
                  : "Failed to load reviews"}
              </p>
            </div>
          )}

          {reviewsData && !reviewsLoading && !reviewsError && (
            <div>
              {reviewsData.reviews.length === 0 ? (
                <div
                  style={{
                    textAlign: "center",
                    color: "var(--color-text-muted)",
                    padding: "32px 0",
                  }}
                >
                  No reviews available for this place
                </div>
              ) : (
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: "16px",
                  }}
                >
                  {reviewsData.reviews.map((review, idx) => (
                    <div
                      key={idx}
                      style={{
                        borderBottom:
                          idx < reviewsData.reviews.length - 1
                            ? "1px solid var(--color-border)"
                            : "none",
                        paddingBottom: "16px",
                      }}
                    >
                      {/* Author info */}
                      <div style={{ marginBottom: "8px" }}>
                        <div
                          style={{
                            fontWeight: "500",
                            color: "var(--color-text)",
                          }}
                        >
                          {review.author}
                        </div>
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "8px",
                            marginTop: "2px",
                          }}
                        >
                          {renderStars(review.rating)}
                          <span
                            style={{
                              fontSize: "12px",
                              color: "var(--color-text-muted)",
                            }}
                          >
                            {review.relativeTime}
                          </span>
                        </div>
                      </div>

                      {/* Review text */}
                      {review.text && (
                        <p
                          style={{
                            color: "var(--color-text-subtle)",
                            fontSize: "14px",
                            lineHeight: "1.6",
                          }}
                        >
                          {review.text}
                        </p>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        {reviewsData && (
          <div
            style={{
              padding: "16px 20px",
              borderTop: "1px solid var(--color-border)",
              background: "var(--color-surface-muted)",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              flexShrink: 0,
            }}
          >
            <p style={{ fontSize: "14px", color: "var(--color-text-muted)" }}>
              Showing {reviewsData.reviews.length} most recent reviews
            </p>
            <a
              href={`https://www.google.com/search?q=${encodeURIComponent(placeData.title + " Reviews")}`}
              target="_blank"
              rel="noopener noreferrer"
              style={{
                padding: "8px 16px",
                background: "var(--color-primary)",
                color: "white",
                borderRadius: "8px",
                fontSize: "14px",
                fontWeight: "500",
                textDecoration: "none",
                transition: "background 0.2s",
              }}
              onMouseEnter={(e) =>
                (e.currentTarget.style.background =
                  "var(--color-primary-hover)")
              }
              onMouseLeave={(e) =>
                (e.currentTarget.style.background = "var(--color-primary)")
              }
            >
              View All on Google
            </a>
          </div>
        )}
      </div>
    );
  }

  // DIRECTIONS VIEW - Interactive map with directions
  if (currentView === "directions") {
    const isMobile =
      typeof window !== "undefined" &&
      window.innerWidth <= BREAKPOINTS.compactMax;

    return (
      <div
        style={{
          height: isMobile ? "100dvh" : "75vh", // Full height on mobile, 75vh on desktop
          width: isMobile ? "100vw" : "100%", // Full width on mobile
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
          position: isMobile ? "fixed" : "relative",
          top: isMobile ? 0 : "auto",
          left: isMobile ? 0 : "auto",
          right: isMobile ? 0 : "auto",
          bottom: isMobile ? 0 : "auto",
          background: "var(--color-surface)",
          borderRadius: isMobile ? "0" : "12px",
          zIndex: isMobile ? 10000 : "auto",
        }}
      >
        {/* Header with back button */}
        <div
          style={{
            padding: "16px",
            borderBottom: "1px solid var(--color-border)",
            display: "flex",
            alignItems: "center",
            gap: "12px",
          }}
        >
          <button
            onClick={() => setCurrentView("main")}
            style={{
              background: "var(--color-surface)",
              border: "1px solid var(--color-border)",
              borderRadius: "50%",
              width: "32px",
              height: "32px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              cursor: "pointer",
            }}
            title="Back"
          >
            <svg
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
            >
              <line x1="19" y1="12" x2="5" y2="12"></line>
              <polyline points="12 19 5 12 12 5"></polyline>
            </svg>
          </button>
          <div>
            <h2 style={{ fontSize: "18px", fontWeight: "600", margin: 0 }}>
              {placeData.title}
            </h2>
            {address && (
              <p
                style={{
                  fontSize: "14px",
                  color: "var(--color-text-muted)",
                  margin: "2px 0 0 0",
                }}
              >
                {address}
              </p>
            )}
          </div>
        </div>

        {/* Map iframe - Using Google Maps Embed API with directions mode */}
        <div style={{ flex: 1, position: "relative" }}>
          {mapsData?.embedUrl ? (
            <iframe
              src={mapsData.embedUrl}
              width="100%"
              height="100%"
              style={{ border: 0 }}
              allowFullScreen
              loading="lazy"
              title={`Directions to ${placeData.title}`}
            />
          ) : (
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                height: "100%",
                color: "var(--color-text-faint)",
              }}
            >
              Loading map...
            </div>
          )}
        </div>

        {/* Footer with action buttons */}
        <div
          style={{
            padding: "16px",
            borderTop: "1px solid var(--color-border)",
            background: "var(--color-surface-muted)",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            flexShrink: 0,
          }}
        >
          <p style={{ fontSize: "14px", color: "var(--color-text-muted)" }}>
            Showing directions from your current location
          </p>
          <a
            href={`https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(destination)}`}
            target="_blank"
            rel="noopener noreferrer"
            style={{
              padding: "8px 16px",
              background: "var(--color-primary)",
              color: "white",
              borderRadius: "8px",
              fontSize: "14px",
              fontWeight: "500",
              textDecoration: "none",
              transition: "background 0.2s",
            }}
            onMouseEnter={(e) =>
              (e.currentTarget.style.background = "var(--color-primary-hover)")
            }
            onMouseLeave={(e) =>
              (e.currentTarget.style.background = "var(--color-primary)")
            }
          >
            Open in Google Maps
          </a>
        </div>
      </div>
    );
  }

  // Extract data for the PlaceCard
  const aboutSection = placeData.sections?.find((s) => s.title === "About");
  const placeCardPhotos = galleryPhotos.map((p) => ({
    url: p.fullsizeUrl || p.thumbnailUrl || "",
    alt: p.caption,
  }));

  // Handle save note — uses apiPatch for proper auth
  const handleAutoSaveNote = async (noteText: string) => {
    if (!placeData.objectId) {
      console.error("[PlaceDetailModule] No objectId — cannot save note");
      return;
    }
    try {
      const { apiPatch: doPatch } = await import("@/lib/api/client");
      await doPatch(
        `/places/${placeData.objectId}`,
        { userNotes: noteText.trim() },
        "*",
      );
      placeData.userNotes = noteText.trim();
      onNoteSaved?.(noteText.trim());
      queryClient.invalidateQueries({
        queryKey: queryKeys.objects.byType("place"),
      });
    } catch (error: any) {
      console.error(
        "[PlaceDetailModule] Failed to save note:",
        error?.message || error,
      );
    }
  };

  // Extract neighborhood from subtitle (e.g., "Paris, 6e")
  const neighborhood =
    placeData.subtitle?.split(",").slice(-1)[0]?.trim() ||
    placeData.subtitle ||
    "";

  // Extract category and open status from chips
  const categoryChip = placeData.chips?.find(
    (c) =>
      !c.includes("Google reviews") &&
      !c.includes("Open") &&
      !c.includes("Closed") &&
      !c.includes("$"),
  );
  const priceLevelChip = placeData.chips?.find((c) => c.includes("$"));
  const isOpenChip = placeData.chips?.find(
    (c) => c.includes("Open") || c.includes("Closed"),
  );
  const ratingChip = placeData.chips?.find((c) => c.includes("Google reviews"));
  const parsedRating = ratingChip?.match(/⭐\s*([\d.]+)/)?.[1];
  const parsedReviewCount = ratingChip?.match(/\(([\d,]+)/)?.[1];

  // MAIN VIEW - Place details (PlaceCard only, no wrapper)
  return (
    <PlaceCard
      name={placeData.title}
      category={categoryChip || undefined}
      priceLevel={priceLevelChip || undefined}
      rating={parsedRating ? parseFloat(parsedRating) : undefined}
      reviewCount={
        parsedReviewCount
          ? parseInt(parsedReviewCount.replace(/,/g, ""), 10)
          : undefined
      }
      isOpen={isOpenChip ? isOpenChip.includes("Open") : undefined}
      description={aboutSection?.body}
      neighborhood={neighborhood}
      photos={placeCardPhotos.length > 0 ? placeCardPhotos : [{ url: "" }]}
      note={placeData.userNotes}
      userRating={localRating}
      ratingSaved={ratingSaved}
      onNoteChange={handleAutoSaveNote}
      onRatingChange={(rating: number) => {
        if (!placeData.objectId) return;
        ratingTouchedRef.current = true;
        const newRating = rating > 0 ? rating : null;
        setLocalRating(newRating ?? undefined);
        setRatingSaved(false);
        // Debounce: update UI instantly, save after 500ms of no changes
        if (ratingSaveTimerRef.current)
          clearTimeout(ratingSaveTimerRef.current);
        ratingSaveTimerRef.current = setTimeout(async () => {
          try {
            const { apiPatch: doPatch } = await import("@/lib/api/client");
            await doPatch(
              `/places/${placeData.objectId}`,
              { userRating: newRating },
              "*",
            );
            setRatingSaved(true);
            setTimeout(() => setRatingSaved(false), 2000);
            queryClient.invalidateQueries({
              queryKey: queryKeys.objects.byType("place"),
            });
          } catch (err) {
            console.error("[PlaceDetailModule] Failed to save rating:", err);
          }
        }, 500);
      }}
      onDirectionsClick={
        directionsAction ? () => setCurrentView("directions") : undefined
      }
      onWebsiteClick={
        websiteAction ? () => handleAction(websiteAction) : undefined
      }
      onCallClick={callAction ? () => handleAction(callAction) : undefined}
    />
  );
}
